import { IOrderClause } from '@tableau/catalog-ui-temp';

import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { isPhoneSizedScreenOrUserProbablyOnMobile } from '~src/commonInfra/utils/MobileHelper';
import { LocationTypes } from '~src/personalSpace/model/ILocationInfo';
import { ContentFilterKeyName, ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { IStringValuedFilterClause } from '~src/spacesPlatform/models/IFilterClause';
import { IExplorableItemTypeInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { LocalSorter } from '~src/spacesPlatform/models/sort/LocalSorter';
import { FilterMap } from '~src/spacesPlatform/state/ContentExplorerState';
import {
  isCurrentSiteUserSpace,
  isPersonalSpace,
  isProjectSpace,
  isSiteSpace,
  isSiteWorkbooksSubspace,
  SpaceInfo,
} from '~src/spacesPlatform/state/SpaceInfo';

import { Messages } from '../../codegen/Localize';
import { IContentInfo } from '../../spacesPlatform/models/IContentInfo';
import { ISpaceItemDetailInfo } from '../../spacesPlatform/models/ISpaceItemDetailInfo';
import { ContentDraftStatus } from '../serverApi/types/ContentDraft';
import {
  ContainerType,
  Draft,
  DraftReferenceType,
  DraftsState,
  DraftWorkbookSpaceProps,
  WorkbookDraftContent,
  WorkbookDraftContentType
} from '../types/DraftsTypes';

export const DraftUtils = {
  areAllFiltersSupported,
  areUnpublishedWorkbooksSupported,
  getFilterValue,
  getUnpublishedWorkbookCount,
  isSpaceWithUnpublishedWorkbookDraftCount,
  isSpaceWithUnpublishedWorkbookDrafts,
  shouldIncludeUnpublishedWorkbookDraftCount,
  shouldIncludeUnpublishedWorkbookDrafts,
  shouldProjectsIncludeUnpublishedWorkbookCount,
};

const SupportedFiltersForUnpublishedWorkbookDrafts: Set<string> = new Set([
  ContentFiltersKeys.createdAfter,
  ContentFiltersKeys.createdBefore,
  ContentFiltersKeys.modifiedAfter,
  ContentFiltersKeys.modifiedBefore,
  ContentFiltersKeys.search,
]);

// TLDR flow drafts that have never been published have a flow id that is mapped to the draft id with a prefix
// Why? Flows and flow drafts are in separate databases on the BE and can have id collisions. These id collisions can cause problems in data grids,
// specifically in the case where a flow draft id collides with a flow id, and they're in the same grid. Flow drafts that have never been
// published don't have a flow associated with them, so they don't have an actual flow id. For these cases, we map the "flow" id to the draftId. The
// prefix is necessary to alleviate the issue with id collisions between flows and flow drafts that have never been published.
const DRAFT_ID_PREFIX: string = 'draftid-';
const contentTypesInOrder = ['project', 'metric', 'workbook', 'flow', 'lens', 'datasource', 'dataRole'];
export const viewsFieldnames = ['hitsLastOneMonthTotal', 'hitsLastThreeMonthsTotal', 'hitsLastTwelveMonthsTotal', 'hitsTotal'];

export function appendDraftIdPrefix(id: string): string {
  return DRAFT_ID_PREFIX + id;
}

// generally useful when you need to call an action specific to flow drafts, the BE can't parse non number strings so we must
// strip off the draft id prefix on the FE
export function removeDraftIdPrefix(id: string): string {
  if (hasDraftIdPrefix(id)) {
    return id.split(DRAFT_ID_PREFIX)[1];
  } else {
    return id;
  }
}

export function hasDraftIdPrefix(id: string): boolean {
  return id.search(DRAFT_ID_PREFIX) !== -1;
}

export function localizeDraftStatus(status: string): string {
  switch (status) {
    case ContentDraftStatus.unpublishedchanges:
      return Messages.draftMarkerUnpublishedChanges();
    case ContentDraftStatus.unpublished:
      return Messages.draftMarkerUnpublished();
    case ContentDraftStatus.none:
    default:
      return '';
  }
}

export function getDraftInfoForHeaderBanner(draftState: DraftsState): DraftWorkbookSpaceProps {
  let draft = draftState.drafts;
  try {
    let draftLuid = Object.keys(draft)[0];  // there should only be one draft in object
    return {
      draftUpdatedAt: draft[draftLuid].updatedAt,
      draftLuid
    };
  } catch (e) {
    return;
  }
}

function isSpaceItemDetailInfoFlow(flow: IContentInfo<'flow'> | ISpaceItemDetailInfo<'flow'>): flow is ISpaceItemDetailInfo<'flow'> {
  return (flow as ISpaceItemDetailInfo<'flow'>).flowDraft !== undefined;
}

export function getFlowDraftId(flow: IContentInfo<'flow'> | ISpaceItemDetailInfo<'flow'>) {
  if (isSpaceItemDetailInfoFlow(flow) && flow.flowDraft.id) {
    return flow.flowDraft.id;
  } else if (flow.draftId) {
    return flow.draftId;
  }
}

export function getUnpublishedWorkbookCount(drafts: Draft[] | WorkbookDraftContent[]): number {
  if (!drafts || drafts.length === 0) {
    return 0;
  }

  if ('draftStatus' in drafts[0]) {
    return getUnpublishedWorkbookCountInWorkbookDrafts(drafts as WorkbookDraftContent[]);
  } else {
    return getUnpublishedWorkbookCountInDrafts(drafts as Draft[]);
  }
}

function getUnpublishedWorkbookCountInDrafts(drafts: Draft[]): number {
  return drafts.reduce((count: number, draft: Draft) => {
    return count + (draft.draftReferenceType === DraftReferenceType.UnpublishedWorkbook ? 1 : 0);
  }, 0);
}

function getUnpublishedWorkbookCountInWorkbookDrafts(workbookDrafts: WorkbookDraftContent[]): number {
  return workbookDrafts.reduce((count: number, workbookDraft: WorkbookDraftContent) => {
    return count + (isUnpublishedDraft(workbookDraft.draftStatus) ? 1 : 0);
  }, 0);
}

export function areUnpublishedWorkbooksSupported() {
  return FeatureFlags.webAuthoringAutoSaveEnabled && FeatureFlags.webAuthoringAutoSaveForNewWorkbooks;
}

export function isSpaceWithUnpublishedWorkbookDrafts(spaceInfo: SpaceInfo) {
  const currentUserId = SessionUserSelectors.sessionUserId(getAppStore().getState());
  return DraftUtils.areUnpublishedWorkbooksSupported() && (
    isPersonalSpace(spaceInfo) ||       // Personal
    isProjectSpace(spaceInfo) ||        // Project
    isCurrentSiteUserSpace(spaceInfo, currentUserId) ||     // My Content
    isSiteWorkbooksSubspace(spaceInfo)  // Explore -> All Workbooks
  );
}

export function isSpaceWithUnpublishedWorkbookDraftCount(spaceInfo: SpaceInfo) {
  const currentUserId = SessionUserSelectors.sessionUserId(getAppStore().getState());
  return DraftUtils.areUnpublishedWorkbooksSupported() && (
    isProjectSpace(spaceInfo) ||        // Project
    isSiteSpace(spaceInfo) ||           // Explore
    isCurrentSiteUserSpace(spaceInfo, currentUserId)   // My Content
  );
}

export function isValidDraftStatus(draftStatus: string): boolean {
  return isPublishedDraft(draftStatus) || isUnpublishedDraft(draftStatus);
}

export function isPublishedDraft(draftStatus: string): boolean {
  return (draftStatus as ContentDraftStatus) === ContentDraftStatus.unpublishedchanges;
}

export function isUnpublishedDraft(draftStatus: string): boolean {
  return (draftStatus as ContentDraftStatus) === ContentDraftStatus.unpublished;
}

export function isUnpublishedWorkbookDraft(item: IContentInfo): boolean {
  return item ? item.contentType === WorkbookDraftContentType && isUnpublishedDraft(item.draftStatus) : false;
}

export function getSortedDraftsAndContent(
  content: ReadonlyArray<IExplorableItemTypeInfo>,
  unpublishedWorkbookDrafts: WorkbookDraftContent[],
  order: IOrderClause[]
): ReadonlyArray<IExplorableItemTypeInfo> {
  const draftSorter = new LocalSorter<IExplorableItemTypeInfo>();
  draftSorter.addTransformForFieldName(transformFieldName);
  draftSorter.addTransformForValue(transformValue);

  unpublishedWorkbookDrafts = draftSorter.sortByClauses(unpublishedWorkbookDrafts, order);
  let sortedContent = sortDraftsIntoAllContent(order, content, unpublishedWorkbookDrafts, draftSorter);
  return sortedContent;
}

function transformFieldName(fieldName: string): string {
  return fieldName === 'contentTypeOrder' ? 'contentType' : fieldName;
}

function transformValue<T>(obj: T, fieldName: string) {
  const isUnpublished = (obj as any).draftStatus === ContentDraftStatus.unpublished;
  let val;

  if (fieldName === 'ownerName') {
    val = (obj as any).owner.displayName;
  }
  else if (fieldName === 'modifiedBy') {
    val = isUnpublished ? (obj as any).owner.displayName : (obj as any).modifiedBy.displayName;
  }
  else if (fieldName === 'numSheets' || fieldName === 'size') {
    val = isUnpublished ? -1 : (obj as any)[fieldName];
  }
  else if (fieldName === 'hasAlert') {
    val = isUnpublished ? false : (obj as any)[fieldName];
  }
  else if (fieldName === 'containerName') {
    if ((obj as any).location === undefined) {
      val = '!';
    }
    else {
      val = (!isUnpublished && (obj as any).location.name === undefined) ? Messages.personalSpaceTitle() : (obj as any).location.name;
    }
  }
  else if (viewsFieldnames.includes(fieldName)) {
    val = isUnpublished ? -1 : (obj as any).usageInfo[fieldName];
  }
  else {
    val = (obj as any)[fieldName];
  }

  // Convert a content type's name into a number based on its expected sort order
  if (fieldName === 'contentType') {
    return contentTypesInOrder.indexOf(val as unknown as string);
  }

  return typeof (val) === 'string' ? val.toLowerCase() : val;
}

// Sort two pre-sorted lists into one combined list
function sortDraftsIntoAllContent(clauses: IOrderClause[], content: ReadonlyArray<IExplorableItemTypeInfo>, sortedDrafts: ReadonlyArray<IExplorableItemTypeInfo>, localSorter: LocalSorter<IExplorableItemTypeInfo>): ReadonlyArray<IExplorableItemTypeInfo> {
  if (!sortedDrafts || sortedDrafts.length === 0) return content;

  if (!clauses || clauses.length === 0) return content.concat(sortedDrafts);

  // W-14678346 - create a sparse array that will hold the combined content items and workbook drafts.
  // we must use a sparse array because this is what is expected throughout VPC.
  // if we insert undefined elements into the allSortedContent array, then filter() and forEach() methods
  // will be executed on undefined elements which causes errors. filter() and foreach() methods ignore
  // array elements that were never explicitly set in sparse arrays
  let allSortedContent: Array<IExplorableItemTypeInfo> = new Array(content.length + sortedDrafts.length);
  let nextSortedElementIndex = 0;

  // iterate over the sparse content array and insert the content items and workbook drafts into the proper positions
  let draftIndex = 0;
  for (var i = 0; i < content.length; i++) {
    if(content[i] === undefined) {
      // content[i] is an array element that was not set in a sparse array
      // we do not want to set the value of allSortedContent[i] to any value
      // to ensure that allSortedContent stays as a sparse array
      nextSortedElementIndex++;
    }
    // insert the next content item if there are no more workbook drafts or if the content item comes before the next workbook draft based on the order clauses
    else if (draftIndex >= sortedDrafts.length || localSorter.compareByClauses(sortedDrafts[draftIndex], content[i], clauses, 0) === 1) {
      allSortedContent[nextSortedElementIndex++] = content[i];
    }
    // insert the next workbook draft into the array
    else {
      allSortedContent[nextSortedElementIndex++] = sortedDrafts[draftIndex++];
      i--;
    }
  }

  // insert remaining workbook drafts
  while (draftIndex < sortedDrafts.length) {
    allSortedContent[nextSortedElementIndex++] = sortedDrafts[draftIndex++];
  }

  return allSortedContent;
}

export function populateLuidToNameAndIDMaps(content: ReadonlyArray<IExplorableItemTypeInfo>, luidToName: Map<string, string>, luidToID: Map<string, string>) {
  for (const item of content) {
    if (item !== undefined && (item as any).location !== undefined && item.contentType === 'workbook') {
      const containerName = (item as any).location.type === LocationTypes.PersonalSpace ? Messages.personalSpaceTitle() : (item as any).location.name;
      luidToName.set((item as any).location.luid, containerName);
      luidToID.set((item as any).location.luid, (item as any).location.id);
    }
  }
}

// removes unpublished workbook drafts from the sparse content array
// we can't use just a normal filter here because filter is not executed on empty elements in sparse array
export function removeUnpublishedDraftsFromContent(content: readonly IExplorableItemTypeInfo[]) {
  const numberOfUnpublishedWorkbookDrafts = content
    .filter(contentItem => isUnpublishedWorkbookDraft((contentItem as IContentInfo)))
    .length;

  if(numberOfUnpublishedWorkbookDrafts === 0) {
    return content;
  }

  const contentWithoutUnpublishedWorkbookDrafts: IExplorableItemTypeInfo[] = new Array(content.length - numberOfUnpublishedWorkbookDrafts);
  let contentIdx = 0;
  // for loop iterates over all elements in the sparse array including empty values
  for(let i = 0; i < content.length; i++) {
    if(!content[i]) {
      contentIdx++;
    } else if(!isUnpublishedWorkbookDraft((content[i] as IContentInfo))) {
      contentWithoutUnpublishedWorkbookDrafts[contentIdx++] = content[i];
    }
  }

  return contentWithoutUnpublishedWorkbookDrafts;
}

export interface WorkbookDraftEditUrlInfo {
  editUrl?: string;
  isUnpublishedWorkbookDraft: boolean;
}

export function extractWorkbookDraftEditUrlInfo(content: IContentInfo): WorkbookDraftEditUrlInfo {
  if (!content || !isUnpublishedDraft(content.draftStatus)) {
    return { isUnpublishedWorkbookDraft: false };
  }

  if (isPhoneSizedScreenOrUserProbablyOnMobile()) {
    return { isUnpublishedWorkbookDraft: true };
  }

  return {
    isUnpublishedWorkbookDraft: true,
    editUrl: (content as any as WorkbookDraftContent).editUrl,
  };
}

export function containerTypeToLocationType(location: ContainerType): LocationTypes {
  switch (location) {
    case ContainerType.Project: {
      return LocationTypes.Project;
    }
    case ContainerType.PersonalSpace: {
      return LocationTypes.PersonalSpace;
    }
    default: {
      return LocationTypes.Unknown;
    }
  }
}

// exported for testing
export function areAllFiltersSupported(filters: FilterMap): boolean {
  let areAllSupported = true;

  Object.keys(filters || {}).forEach(filterKey => {
    if (!SupportedFiltersForUnpublishedWorkbookDrafts.has(filterKey)) {
      areAllSupported = false;
    }
  });

  return areAllSupported;
}

export function getFilterValue(filters: FilterMap, filterKey: ContentFilterKeyName): string {
  return filters && filters[filterKey] ? (filters[filterKey] as IStringValuedFilterClause).value : undefined;
}

export function shouldIncludeUnpublishedWorkbookDrafts(filters: FilterMap, spaceInfo: SpaceInfo): boolean {
  return DraftUtils.areAllFiltersSupported(filters) && DraftUtils.isSpaceWithUnpublishedWorkbookDrafts(spaceInfo);
}

export function shouldIncludeUnpublishedWorkbookDraftCount(filters: FilterMap, spaceInfo: SpaceInfo) {
  return DraftUtils.areAllFiltersSupported(filters) && DraftUtils.isSpaceWithUnpublishedWorkbookDraftCount(spaceInfo);
}

export function shouldProjectsIncludeUnpublishedWorkbookCount(contentTypes: ReadonlyArray<ExplorableItemType>) {
  return DraftUtils.areUnpublishedWorkbooksSupported() && contentTypes.length === 1 && contentTypes[0] === 'project';
}

